import { supabase } from '../lib/supabase.js';

class SharingService {
  // Detect and mark content updates for shared items
  async detectContentUpdate(itemType, itemId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get student's family member ID
      const { data: studentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!studentMember) return;

      // Update all active sharing permissions for this item
      const { data, error } = await supabase
        .from('sharing_permissions')
        .update({
          last_content_update: new Date().toISOString(),
          revision_count: supabase.raw('revision_count + 1'),
          unread_updates: true
        })
        .eq('student_id', studentMember.id)
        .eq('item_type', itemType)
        .eq('item_id', itemId)
        .is('revoked_at', null);

      if (!error && data && data.length > 0) {
        // Create notifications for each parent
        const notifications = data.map(permission => ({
          recipient_id: permission.shared_with_id,
          sender_id: studentMember.id,
          notification_type: 'item_updated',
          item_type: itemType,
          item_id: itemId,
          item_title: permission.item_title,
          message: 'Content has been updated'
        }));

        await supabase.from('sharing_notifications').insert(notifications);
      }

      return { updated: true, count: data?.length || 0 };
    } catch (error) {
      console.error('Error detecting content update:', error);
      return { updated: false };
    }
  }

  // Share an item with parents or other family members
  async shareItem(itemType, itemId, recipients, options = {}) {
    console.log('🟢 SharingService: shareItem called with:', {
      itemType,
      itemId,
      recipients,
      options
    });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('🟢 SharingService: Current user:', user?.id);
      if (!user) throw new Error('User not authenticated');

      // Get student's family member ID
      const { data: studentMember, error: memberError } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      console.log('🟢 SharingService: Student member lookup:', {
        studentMember,
        memberError
      });

      if (memberError) throw memberError;

      // Create sharing permissions for each recipient
      const sharingPromises = recipients.map(recipientId => {
        const shareData = {
          student_id: studentMember.id,
          shared_with_id: recipientId,
          item_type: itemType,
          item_id: itemId,
          item_title: options.itemTitle || null,
          permission_level: options.permissionLevel || 'view',
          version: options.version || 'current',
          allow_comments: options.allowComments !== false,
          notify_on_view: options.notifyOnView || false,
          metadata: options.metadata || {},
          revoked_at: null // Clear any previous revocation
        };

        console.log('🟢 SharingService: Creating sharing permission with:', shareData);

        return supabase
          .from('sharing_permissions')
          .upsert(shareData, {
            onConflict: 'student_id,shared_with_id,item_type,item_id'
          })
          .select();
      });

      const results = await Promise.all(sharingPromises);

      console.log('🟢 SharingService: Upsert results:', results);

      // Check for errors in results
      let hasErrors = false;
      results.forEach((result, index) => {
        if (result.error) {
          console.error(`🔴 SharingService: Error for recipient ${recipients[index]}:`, result.error);
          hasErrors = true;
        } else {
          console.log(`🟢 SharingService: Success for recipient ${recipients[index]}:`, result.data);
        }
      });

      if (hasErrors) {
        throw new Error('Some sharing operations failed. Check console for details.');
      }

      // Verify the data was actually inserted
      const { data: verifyData, error: verifyError } = await supabase
        .from('sharing_permissions')
        .select('*')
        .eq('student_id', studentMember.id)
        .eq('item_id', itemId)
        .eq('item_type', itemType);

      console.log('🟢 SharingService: Verification query:', {
        verifyData,
        verifyError,
        query: {
          student_id: studentMember.id,
          item_id: itemId,
          item_type: itemType
        }
      });

      return {
        success: true,
        sharedAt: new Date().toISOString(),
        sharedWith: recipients,
        message: 'Item shared successfully',
        verificationData: verifyData
      };
    } catch (error) {
      console.error('🔴 SharingService: Error sharing item:', error);
      throw error;
    }
  }

  // Revoke sharing for an item
  async revokeSharing(itemType, itemId, recipientId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: studentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      let query = supabase
        .from('sharing_permissions')
        .update({ revoked_at: new Date().toISOString() })
        .eq('student_id', studentMember.id)
        .eq('item_type', itemType)
        .eq('item_id', itemId)
        .is('revoked_at', null);

      if (recipientId) {
        query = query.eq('shared_with_id', recipientId);
      }

      const { error } = await query;
      if (error) throw error;

      return {
        success: true,
        message: 'Sharing revoked successfully'
      };
    } catch (error) {
      console.error('Error revoking share:', error);
      throw error;
    }
  }

  // Get all items shared by current student
  async getSharedItemsByStudent() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: studentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      const { data, error } = await supabase
        .from('sharing_permissions')
        .select(`
          *,
          shared_with:family_members!shared_with_id(
            id,
            name,
            role
          ),
          comments:parent_comments(count)
        `)
        .eq('student_id', studentMember.id)
        .is('revoked_at', null)
        .order('shared_at', { ascending: false });

      if (error) throw error;

      // Group by item type for easier display
      const grouped = data.reduce((acc, item) => {
        if (!acc[item.item_type]) acc[item.item_type] = [];
        acc[item.item_type].push(item);
        return acc;
      }, {});

      return grouped;
    } catch (error) {
      console.error('Error getting shared items:', error);
      throw error;
    }
  }

  // Get all items shared with current parent
  async getSharedItemsForParent() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: parentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      // Use the database function for better performance
      const { data, error } = await supabase
        .rpc('get_shared_items_for_parent', {
          p_parent_id: parentMember.id
        });

      if (error) throw error;

      // Fetch actual content for each item type
      const enrichedData = await this.enrichSharedItems(data);

      return enrichedData;
    } catch (error) {
      console.error('Error getting shared items for parent:', error);
      throw error;
    }
  }

  // Enrich shared items with actual content
  async enrichSharedItems(sharedItems) {
    const enrichedItems = [];

    for (const item of sharedItems) {
      let content = null;
      let additionalData = {};

      switch (item.item_type) {
        case 'essay':
          // Try college_journey_essays first
          const { data: essay } = await supabase
            .from('college_journey_essays')
            .select(`
              *,
              college:college_journey_colleges(
                name,
                category
              )
            `)
            .eq('id', item.item_id)
            .single();
          content = essay;
          break;

        case 'freewrite':
          const { data: freewrite } = await supabase
            .from('college_journey_freewrites')
            .select('*')
            .eq('id', item.item_id)
            .single();
          content = freewrite;
          break;

        case 'brag_sheet':
          // Get brag sheet data from student_academic_profiles
          const { data: bragSheetData } = await supabase
            .rpc('get_brag_sheet_for_student', {
              p_student_id: item.student_id
            });
          content = bragSheetData;
          break;

        case 'recommendation':
          const { data: recommendation } = await supabase
            .from('college_recommendation_letters')
            .select(`
              *,
              recommender_name,
              recommender_role,
              subject_area
            `)
            .eq('id', item.item_id)
            .single();
          content = recommendation;
          break;

        case 'timeline':
          // For timeline, get summary statistics
          const { data: timelineStats } = await supabase
            .from('timeline_items')
            .select('status')
            .eq('user_id', item.student_id);

          if (timelineStats) {
            const total = timelineStats.length;
            const completed = timelineStats.filter(t => t.status === 'completed').length;
            additionalData = {
              completionRate: Math.round((completed / total) * 100),
              totalTasks: total,
              completedTasks: completed
            };
          }
          break;

        case 'college_list':
          // Get college list for the student
          const { data: colleges } = await supabase
            .from('college_journey_colleges')
            .select('*')
            .eq('user_id', item.student_id)
            .is('removed_at', null);
          content = colleges;
          break;
      }

      // Get comments for this shared item
      const { data: comments } = await supabase
        .from('parent_comments')
        .select(`
          *,
          parent:family_members!parent_id(
            name
          )
        `)
        .eq('sharing_permission_id', item.id)
        .order('created_at', { ascending: false });

      enrichedItems.push({
        ...item,
        content,
        additionalData,
        comments: comments || []
      });
    }

    return enrichedItems;
  }

  // Get sharing permissions for current student
  async getSharingPermissions() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: studentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      // Get all parent access settings
      const { data, error } = await supabase
        .from('parent_access_settings')
        .select(`
          *,
          parent:family_members!parent_id(
            id,
            name,
            role
          )
        `)
        .eq('student_id', studentMember.id);

      if (error) throw error;

      // Format permissions by category
      const permissions = {
        essays: {
          enabled: data.some(d => d.essays_enabled),
          mode: data[0]?.essays_mode || 'selective',
          autoShareFinal: data[0]?.essays_auto_share_final || false
        },
        bragSheet: {
          enabled: data.some(d => d.brag_sheet_enabled),
          mode: data[0]?.brag_sheet_mode || 'selective'
        },
        recommendations: {
          enabled: data.some(d => d.recommendations_enabled),
          mode: data[0]?.recommendations_mode || 'selective'
        },
        timeline: {
          enabled: data.some(d => d.timeline_enabled),
          showCompletion: data[0]?.timeline_show_completion !== false
        },
        collegeList: {
          enabled: data.some(d => d.college_list_enabled),
          showStatus: data[0]?.college_list_show_status !== false,
          showEssays: data[0]?.college_list_show_essays || false
        },
        profile: {
          enabled: data.some(d => d.profile_enabled),
          sections: data[0]?.profile_sections || []
        }
      };

      return { permissions, parentAccess: data };
    } catch (error) {
      console.error('Error getting sharing permissions:', error);
      throw error;
    }
  }

  // Update sharing permissions for a category
  async updateSharingPermissions(parentId, category, settings) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: studentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      // Map category to database columns
      const columnMap = {
        essays: {
          [`${category}_enabled`]: settings.enabled,
          [`${category}_mode`]: settings.mode || 'selective',
          [`${category}_auto_share_final`]: settings.autoShareFinal || false
        },
        bragSheet: {
          'brag_sheet_enabled': settings.enabled,
          'brag_sheet_mode': settings.mode || 'selective'
        },
        recommendations: {
          [`${category}_enabled`]: settings.enabled,
          [`${category}_mode`]: settings.mode || 'selective'
        },
        timeline: {
          [`${category}_enabled`]: settings.enabled,
          [`${category}_show_completion`]: settings.showCompletion !== false
        },
        collegeList: {
          'college_list_enabled': settings.enabled,
          'college_list_show_status': settings.showStatus !== false,
          'college_list_show_essays': settings.showEssays || false
        },
        profile: {
          [`${category}_enabled`]: settings.enabled,
          [`${category}_sections`]: settings.sections || []
        }
      };

      const updates = columnMap[category] || {};

      const { error } = await supabase
        .from('parent_access_settings')
        .upsert({
          student_id: studentMember.id,
          parent_id: parentId,
          ...updates
        }, {
          onConflict: 'student_id,parent_id'
        });

      if (error) throw error;

      return {
        success: true,
        message: 'Permissions updated successfully'
      };
    } catch (error) {
      console.error('Error updating sharing permissions:', error);
      throw error;
    }
  }

  // Add parent comment to a shared item
  async addParentComment(sharingPermissionId, commentText) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: parentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      const { data, error } = await supabase
        .from('parent_comments')
        .insert({
          sharing_permission_id: sharingPermissionId,
          parent_id: parentMember.id,
          comment_text: commentText,
          is_supportive: true
        })
        .select()
        .single();

      if (error) throw error;

      // Create notification for the student
      const { data: permission } = await supabase
        .from('sharing_permissions')
        .select('student_id, item_type, item_title')
        .eq('id', sharingPermissionId)
        .single();

      if (permission) {
        await supabase
          .from('sharing_notifications')
          .insert({
            recipient_id: permission.student_id,
            sender_id: parentMember.id,
            notification_type: 'comment_added',
            item_type: permission.item_type,
            item_id: sharingPermissionId,
            item_title: permission.item_title,
            message: `New comment on your ${permission.item_type}`
          });
      }

      return data;
    } catch (error) {
      console.error('Error adding parent comment:', error);
      throw error;
    }
  }

  // Mark item as viewed by parent
  async markAsViewed(sharingPermissionId) {
    try {
      const { error } = await supabase
        .from('sharing_permissions')
        .update({ viewed_at: new Date().toISOString() })
        .eq('id', sharingPermissionId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error marking as viewed:', error);
      throw error;
    }
  }

  // Get notifications for current user
  async getNotifications(unreadOnly = false) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: member } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!member) throw new Error('Family member record not found');

      let query = supabase
        .from('sharing_notifications')
        .select(`
          *,
          sender:family_members!sender_id(
            name
          )
        `)
        .eq('recipient_id', member.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (unreadOnly) {
        query = query.eq('read', false);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error getting notifications:', error);
      throw error;
    }
  }

  // Mark notifications as read
  async markNotificationsAsRead(notificationIds) {
    try {
      const { error } = await supabase
        .from('sharing_notifications')
        .update({ read: true })
        .in('id', notificationIds);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      throw error;
    }
  }
}

export default new SharingService();